import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notification } from '@ohpen/enso-ui';
import type { ServerSideRequest, TableColumns, ValueLists } from '@ohpen/enso-ui';

import {
  getApiErrorMessage,
  SQE_MAX_PAGE_SIZE,
  toSqeQuery,
  unqueriedColumns,
  type SqeFields,
  type SqePage,
  type SqeQuery,
  type SqeSort,
} from '@pmp/utils';

/** The most rows an export pulls page by page before it stops, unless the list sets its own limit. */
const MAX_EXPORT_ROWS = 20000;

/** How many export pages are in flight at once. */
const EXPORT_CONCURRENCY = 4;

export type SqeListProps<T> = {
  isFetching: boolean;
  data: T[];
  total: number;
  setServerSideFiltering: (request: ServerSideRequest) => void;
  exportFetchAll: (scope: 'view' | 'all') => Promise<Record<string, unknown>[]>;
  refresh: () => void;
};

/**
 * Serves a server-side enso table from a structured query endpoint: the table reports what it asks for, this turns
 * it into the SQE body and keeps the page that answers the latest request, so a slow answer to an older request
 * never replaces a newer one.
 */
export const useSqeList = <T, N>(
  fetchPage: (query: SqeQuery) => Promise<SqePage<T>>,
  columns: TableColumns<T>,
  sqeFields: SqeFields<N>,
  valueLists?: ValueLists,
  defaultSort?: SqeSort,
  maxExportRows = MAX_EXPORT_ROWS,
): SqeListProps<T> => {
  const [page, setPage] = useState<SqePage<T>>({ items: [], total: 0 });
  const [isFetching, setIsFetching] = useState(false);
  const { t } = useTranslation();
  const lastRequest = useRef<ServerSideRequest | undefined>(undefined);
  const lastQuery = useRef<string | undefined>(undefined);
  const latestCall = useRef(0);
  const lastFetch = useRef<typeof fetchPage | undefined>(undefined);

  // Read when a request is built rather than captured, so labels that load after the table still reach the search.
  const toQuery = (request: ServerSideRequest | undefined) => toSqeQuery(request, columns, sqeFields, valueLists, defaultSort);
  const queryOf = useRef(toQuery);
  queryOf.current = toQuery;
  const unqueriedOf = useRef((request: ServerSideRequest) => unqueriedColumns(request, columns, sqeFields));
  unqueriedOf.current = (request: ServerSideRequest) => unqueriedColumns(request, columns, sqeFields);
  const lastUnqueried = useRef('');

  const load = useCallback(
    async (request: ServerSideRequest | undefined) => {
      const call = ++latestCall.current;
      const query = queryOf.current(request);
      lastQuery.current = JSON.stringify(query);
      lastFetch.current = fetchPage;
      setIsFetching(true);
      try {
        const result = await fetchPage(query);
        if (call === latestCall.current) setPage(result);
      } catch (error) {
        if (call === latestCall.current) {
          // The rows of the previous request do not answer this one, so they are not left under its filter.
          setPage({ items: [], total: 0 });
          notification().error({ title: getApiErrorMessage(error) });
        }
      } finally {
        if (call === latestCall.current) setIsFetching(false);
      }
    },
    [fetchPage],
  );

  // A filter or sort the search cannot run, as a saved view or a link can still carry, is said once rather than
  // leaving the rows looking filtered by it. Every page of the same request would say it again otherwise.
  const warnUnqueried = useCallback(
    (request: ServerSideRequest) => {
      const titles = unqueriedOf.current(request).map(title => t(title, { defaultValue: title }));
      const said = titles.join('\n');
      if (said === lastUnqueried.current) return;
      lastUnqueried.current = said;
      if (titles.length > 0) notification().warning({ title: t('global.notifications.unqueriedColumns', { columns: titles.join(', ') }) });
    },
    [t],
  );

  const setServerSideFiltering = useCallback(
    (request: ServerSideRequest) => {
      lastRequest.current = request;
      warnUnqueried(request);
      void load(request);
    },
    [load, warnUnqueried],
  );

  const refresh = useCallback(() => void load(lastRequest.current), [load]);

  // A fetch that answers differently, such as one narrowed by filters outside the table, is asked the table's last
  // request again, unless the table has already sent it a request of its own, as it does when it moves to page 1.
  useEffect(() => {
    if (lastRequest.current === undefined || lastFetch.current === fetchPage) return;
    load(lastRequest.current);
  }, [fetchPage, load]);

  // A search sent before the value lists arrived matched no labels; it is sent again once they can change it.
  useEffect(() => {
    if (lastRequest.current === undefined) return;
    if (JSON.stringify(queryOf.current(lastRequest.current)) !== lastQuery.current) void load(lastRequest.current);
  }, [valueLists, load]);

  const exportFetchAll = useCallback(
    async (scope: 'view' | 'all') => {
      const base = queryOf.current(scope === 'view' ? lastRequest.current : undefined);
      const pageOf = (current: number) => fetchPage({ ...base, page: current, pageSize: SQE_MAX_PAGE_SIZE });

      const fetchAll = async (): Promise<SqePage<T>> => {
        const first = await pageOf(1);
        const items: T[] = [...first.items];
        const lastPage = Math.ceil(Math.min(first.total, maxExportRows) / SQE_MAX_PAGE_SIZE);
        for (let start = 2; start <= lastPage; start += EXPORT_CONCURRENCY) {
          const batch = Array.from({ length: Math.min(EXPORT_CONCURRENCY, lastPage - start + 1) }, (_, index) => start + index);
          const pages = await Promise.all(batch.map(pageOf));
          pages.forEach(page => items.push(...page.items));
        }
        return { items, total: first.total };
      };

      let exported: SqePage<T>;
      try {
        exported = await fetchAll();
      } catch (error) {
        // The export button only logs a failure, so the user is told here.
        notification().error({ title: getApiErrorMessage(error) });
        throw error;
      }

      if (exported.total > maxExportRows) {
        notification().warning({
          title: t('global.notifications.exportTruncated', { count: maxExportRows, total: exported.total }),
        });
      }
      return exported.items.slice(0, maxExportRows) as Record<string, unknown>[];
    },
    [fetchPage, maxExportRows, t],
  );

  return useMemo(
    () => ({ isFetching, data: page.items, total: page.total, setServerSideFiltering, exportFetchAll, refresh }),
    [isFetching, page, setServerSideFiltering, exportFetchAll, refresh],
  );
};
